import React from 'react';
import { Badge } from 'react-bootstrap';
import { AlertTriangle } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import { WORK_ORDER_LABELS as L, workOrderSectionTestId } from '../../workOrderSections';
import { ReportedIssueState } from '../../../../types/issues';
import './shared.css';

export interface ReportedIssueRow {
    key: string;
    description: string;
    /** Component name; absent = "Annat" */
    componentName?: string;
    thumbnailUrl?: string;
    state: ReportedIssueState;
    /** Where the row links to (the issue, or the work order it became); absent = not clickable */
    href?: string;
}

export interface ReportedIssuesSectionProps {
    /** Opens the report form; absent hides the Felanmäl button */
    onReport?: () => void;
    /** Internal users: the order's reports. Absent on the guest page. */
    rows?: ReportedIssueRow[];
    /** Guest page: how many reports this guest has made from the order */
    ownReportCount?: number;
}

const STATE_LABELS: Record<ReportedIssueState, { label: string; bg: string }> = {
    active: { label: L.reportedIssueActive, bg: 'primary' },
    converted: { label: L.reportedIssueConverted, bg: 'success' },
    archived: { label: L.reportedIssueArchived, bg: 'secondary' },
};

const truncate = (text: string, max = 80) => (text.length <= max ? text : `${text.substring(0, max)}…`);

/** Own reports on the guest page, in words. */
export const ownReportCountText = (count: number): string =>
    count === 1
        ? 'Du har gjort 1 felanmälan från detta ärende.'
        : `Du har gjort ${count} felanmälningar från detta ärende.`;

/**
 * Fel hittade under arbetet (not the felanmälan the order came from): the
 * Felanmäl nytt fel action while the order is open, with the
 * order's reports (internal users) or the guest's own count (guest page).
 * Read-only: a report is handled as an issue, never edited here.
 */
export const ReportedIssuesSection: React.FC<ReportedIssuesSectionProps> = ({ onReport, rows, ownReportCount }) => (
    <div className="wo-reported-issues wo-card" data-testid={workOrderSectionTestId('reportedIssues')}>
        <SectionTitle
            icon={AlertTriangle}
            title={L.sectionReportedIssues}
            count={rows?.length}
            onAdd={onReport}
            addLabel={L.reportIssue}
        />
        {rows !== undefined && rows.length > 0 ? (
            <ul className="list-unstyled mb-0">
                {rows.map((row) => {
                    const state = STATE_LABELS[row.state];
                    const content = (
                        <>
                            {row.thumbnailUrl ? (
                                <img src={row.thumbnailUrl} alt="" loading="lazy" className="wo-reported-issues__thumb" />
                            ) : (
                                <div className="wo-reported-issues__thumb wo-reported-issues__thumb--empty" aria-hidden="true">
                                    <AlertTriangle size={16} />
                                </div>
                            )}
                            <div className="flex-grow-1 wo-reported-issues__text">
                                <div>{truncate(row.description)}</div>
                                <small className="text-muted">{row.componentName || L.reportedIssueNoComponent}</small>
                            </div>
                            <Badge bg={state.bg} className="flex-shrink-0">{state.label}</Badge>
                        </>
                    );
                    return (
                        <li key={row.key} className="py-1">
                            {row.href ? (
                                <a href={row.href} className="wo-reported-issues__row wo-reported-issues__row--link">{content}</a>
                            ) : (
                                <div className="wo-reported-issues__row">{content}</div>
                            )}
                        </li>
                    );
                })}
            </ul>
        ) : ownReportCount !== undefined && ownReportCount > 0 ? (
            <p className="small mb-0">{ownReportCountText(ownReportCount)}</p>
        ) : (
            <p className="text-muted small mb-0">{L.reportedIssuesEmpty}</p>
        )}
    </div>
);

export default ReportedIssuesSection;
